import { TextInput, Button } from '@pandiafi/design-system'
import { Formik, Field, FieldProps, Form } from 'formik'
import { t } from 'i18next'
import React from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Text } from '@pandiafi/components'

import {
  SetVerificationCodeEmailRequest,
  setVerificationCodeEmailRequestSchema,
} from '../../../../common/validations'
import { useAPIResendVerificationCode } from '../../../api/apiHooks/useAPIResendVerificationCode'
import FormFieldWrapper from '../../../components/FormField'
import routePath from '../../../router/routePath'
import { validateFormValues } from '../../../utils'
import { FormErrorSection } from '../../Login/FormErrorSection'
import { validateEmail } from '../../Login/utils'
import { PageTranslationSafeLabel } from '../../../components'

interface EmailVerificationFormValues {
  email: string
}

export const EmailVerificationForm: React.FC<{
  onSuccess: (email: string) => void
}> = ({ onSuccess }) => {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { mutate } = useAPIResendVerificationCode()
  const [error, setError] = React.useState('')

  // Email prop can be passed in the URL
  const emailParam = params.get('email')

  // If email prop is invalid, show empty input field
  const email = emailParam ? validateEmail(emailParam) : ''

  const handleValidate = (formValues: SetVerificationCodeEmailRequest) => {
    const validationErrors = validateFormValues(
      formValues,
      setVerificationCodeEmailRequestSchema
    )

    return validationErrors
  }

  return (
    <Formik
      initialValues={{
        email: email || '',
      }}
      validate={handleValidate}
      validateOnMount
      onSubmit={(data: EmailVerificationFormValues) => {
        mutate(
          { email: data.email },
          {
            onSuccess: () => {
              onSuccess(data.email)
            },
            onError: () => {
              setError(t('registration:verificationCodeResendError'))
            },
          }
        )
      }}
    >
      {({ isValid, isSubmitting, setFieldValue, setFieldTouched }) => (
        <Form>
          <div className="flex flex-col gap-m">
            <Text size="l" weight="medium" element="h3">
              {t('registration:confirmEmailHeader')}
            </Text>
            <Text size="m" element="p">
              {t('registration:confirmEmailDescription')}
            </Text>
            <div>
              <Field name="email">
                {({
                  field,
                  meta,
                }: FieldProps<EmailVerificationFormValues['email']>) => (
                  <FormFieldWrapper
                    name={field.name}
                    meta={meta}
                    label={t('common:email')}
                  >
                    <TextInput
                      {...field}
                      onChange={async (e) => {
                        const lowercaseValue = e.target.value.toLowerCase()
                        await setFieldValue('email', lowercaseValue)
                        await setFieldTouched('email', true)
                      }}
                    />
                  </FormFieldWrapper>
                )}
              </Field>
            </div>

            {error && <FormErrorSection error={error} />}

            <Button
              disabled={!isValid || isSubmitting}
              loading={isSubmitting}
              type="submit"
            >
              <PageTranslationSafeLabel>
                {t('registration:confirmEmailSubmit')}
              </PageTranslationSafeLabel>
            </Button>

            <Button
              type="button"
              variant="cancel"
              onClick={() => {
                navigate(routePath.login)
              }}
            >
              <PageTranslationSafeLabel>
                {t('common:cancel')}
              </PageTranslationSafeLabel>
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  )
}
